import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  EMAIL_REGEX,
  GOV_DELIVERY_ROUTE,
  STREETCAR_GOV_DELIVERY_TOPIC,
  STREETCAR_ROUTES,
} from '#/shared/constants';
import useTriMetStaticPageLinks from '#/shared/hooks/useTriMetStaticPageLinks';
import * as Styled from './styled';

function RouteAlertsSignupForm({ routeId, routeName, focusEmailInput }) {
  const intl = useIntl();
  const [showError, setError] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const emailInput = useRef(null);
  const updateEmailAddress = event => {
    setEmailAddress(event.target.value);
  };

  // We only have one GovDelivery 'Topic' for all streetcar routes currently. Thus we map it here.
  let mappedRouteId = routeId;
  if (STREETCAR_ROUTES.includes(mappedRouteId)) {
    mappedRouteId = STREETCAR_GOV_DELIVERY_TOPIC;
  }

  const subscribe = event => {
    event.preventDefault();
    event.currentTarget.submit();
  };

  useEffect(() => {
    if (focusEmailInput) emailInput.current.focus();
  }, [focusEmailInput]);

  useEffect(() => {
    if (emailAddress) {
      if (!EMAIL_REGEX.test(emailAddress)) {
        setError(true);
      } else {
        setError(false);
      }
    }
  }, [emailAddress]);

  const { trimetLinks } = useTriMetStaticPageLinks();

  return (
    <Styled.SignupForm
      action={GOV_DELIVERY_ROUTE}
      data-testid="signupForm"
      method="POST"
      target="_blank"
      onSubmit={subscribe}
    >
      <Styled.SignupFormMessage>
        <Styled.EmailIcon />
        <Styled.SignupFormMessageText>
          <FormattedMessage
            id="alerts.routealertsemail"
            values={{ route: routeName }}
          />
        </Styled.SignupFormMessageText>
      </Styled.SignupFormMessage>
      <input
        type="hidden"
        name="topic_id"
        value={`ORTRIMET_${mappedRouteId.padStart(3, '0')}`}
      />
      <input type="hidden" name="origin" value={trimetLinks.max} />
      <input type="hidden" name="email" id="email" value={emailAddress} />
      <Styled.SignupFormInput
        onChange={e => updateEmailAddress(e)}
        placeholder={intl.formatMessage({ id: 'alerts.enteremail' })}
        ref={emailInput}
        data-testid="emailInput"
        spellCheck="false"
        autoCapitalize="off"
        autoComplete="email"
        type="email"
        required
      />
      {showError && (
        <Styled.SignupFormError data-testid="errorMessage">
          <FormattedMessage id="alerts.invalid-email" />
        </Styled.SignupFormError>
      )}
      <Styled.SignupFormButton
        disabled={showError || emailAddress === ''}
        data-testid="submitButton"
      >
        <FormattedMessage id="alerts.signup" />
      </Styled.SignupFormButton>
    </Styled.SignupForm>
  );
}

RouteAlertsSignupForm.propTypes = {
  /** The Id of the route */
  routeId: PropTypes.string.isRequired,
  /** The name of the route */
  routeName: PropTypes.string.isRequired,
  /** Used to focus the email input externally */
  focusEmailInput: PropTypes.bool,
};

RouteAlertsSignupForm.defaultProps = {
  focusEmailInput: false,
};

export default RouteAlertsSignupForm;
